import { useTheme } from "next-themes";
import { Toaster as Sonner, toast } from "sonner";
import { errorHelpText } from "@/lib/errorHelpText";
import { logFrontendError } from "@/lib/errorLogger";

const isAdminPath = () => typeof window !== "undefined" && window.location.pathname.startsWith("/admin");
const textOf = (v: any) => (typeof v === "string" ? v : v == null ? "" : String((v as any)?.message ?? ""));

// Browser/extension noise that is not an app failure.
// Also: stale-file errors after a new publish (page reloads itself automatically).
const NOISE = /ResizeObserver loop|Script error\.?$|Load failed$|NetworkError when attempting|AbortError|cancelled|canceled|chrome-extension:|moz-extension:|Non-Error promise rejection|dynamically imported module|Importing a module script failed|error loading dynamically|ChunkLoadError|Loading chunk \d+ failed|Loading CSS chunk|\["@context"\]|The object can not be found here/i;

if (typeof window !== "undefined" && !(window as any).__errLogInit) {
  (window as any).__errLogInit = true;
  window.addEventListener("error", (e) => {
    if (isAdminPath() || !e.message || NOISE.test(e.message)) return;
    logFrontendError("window.error", e.message, { url: location.href, file: e.filename, line: e.lineno });
  });
  window.addEventListener("unhandledrejection", (e) => {
    if (isAdminPath()) return;
    const msg = textOf(e.reason) || "Unhandled promise rejection";
    if (NOISE.test(msg)) return;
    logFrontendError("unhandledrejection", msg, { url: location.href });
  });
}

type ToasterProps = React.ComponentProps<typeof Sonner>;

// Normal user-flow messages (validation, login prompts, missing fields).
// These are NOT technical failures: no screenshot hint, no CMS error alert.
const EXPECTED_PATTERNS: RegExp[] = [
  /zaloguj|logowanie|log in|sign in|anmelde|connecte|accedi/i,
  /nieprawid[łl]ow|b[łl][ąa]d walidacji|invalid|validation|ung[üu]ltig|obligatoire|non valido/i,
  /wymagan|required|erforderlich|requis|richiesto/i,
  /uzupe[łl]nij|wype[łl]nij|fill in|bitte ausf[üu]llen|remplis|compila/i,
  /has[łl]o|password|passwort|mot de passe|password errata/i,
  /e-?mail .*(nieprawid|invalid|ung[üu]ltig)/i,
  /wybierz|select|choisis|w[äa]hle|seleziona/i,
  /pusty koszyk|koszyk jest pusty|cart is empty|panier vide|carrello vuoto|warenkorb ist leer/i,
  /maksymaln|minimaln|za du[żz]y|too large|too many|maximum|minimum/i,
  /wpisz|podaj|please enter|veuillez entrer|bitte geben|inserisci/i,
];

const isExpectedUserMessage = (text: string) =>
  !!text && EXPECTED_PATTERNS.some((re) => re.test(text));

// Append the "send us a screenshot" hint to real technical error toasts only.
const originalError = toast.error;
if (!(originalError as any).__withHelp) {
  const patched = ((message: any, data?: any) => {
    const msgText = textOf(message) || "Błąd";
    const descText = textOf(data?.description);
    const expected = isExpectedUserMessage(msgText) || isExpectedUserMessage(descText);

    if (expected) {
      return originalError(message, { duration: 6000, ...data });
    }

    if (!isAdminPath()) {
      logFrontendError("toast.error", msgText, { url: location.href, description: descText });
    }
    const extra = errorHelpText();
    const description = data?.description ? (
      <span className="block">
        <span className="block">{data.description}</span>
        <span className="mt-2 block font-medium">{extra}</span>
      </span>
    ) : (
      extra
    );
    return originalError(message, { duration: 12000, ...data, description });
  }) as typeof toast.error;
  (patched as any).__withHelp = true;
  (toast as any).error = patched;
}

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme();

  return (
    <Sonner
      theme={theme as ToasterProps["theme"]}
      position="top-center"
      closeButton
      className="toaster group"
      style={{ zIndex: 2147483647 }}
      toastOptions={{
        style: { width: "min(560px, 92vw)" },
        classNames: {
          toast:
            "group toast text-base p-5 group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-2 group-[.toaster]:border-border group-[.toaster]:shadow-2xl",
          title: "text-lg font-semibold",
          description: "text-base group-[.toast]:text-foreground/80",
          error: "group-[.toaster]:border-destructive",
          success:
            "group-[.toaster]:!bg-primary group-[.toaster]:!text-primary-foreground group-[.toaster]:!border-primary-foreground/40 !text-xl font-bold shadow-[0_0_30px_hsl(var(--primary)/0.6)] animate-[pulse_0.9s_ease-in-out_3]",
          actionButton: "group-[.toast]:bg-primary group-[.toast]:text-primary-foreground",
          cancelButton: "group-[.toast]:bg-muted group-[.toast]:text-muted-foreground",
        },
      }}
      {...props}
    />
  );
};

export { Toaster, toast };
